import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Edit, Save, X, Image, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import MediaLibrary from './MediaLibrary';

interface ContentManagerProps {
  section: string;
  pageId?: string;
  initialContent?: {
    title?: string;
    subtitle?: string;
    description?: string;
    image?: string;
    items?: Array<{
      id: string;
      title: string;
      description: string;
      image?: string;
    }>;
  };
}

const ContentManager: React.FC<ContentManagerProps> = ({
  section,
  pageId,
  initialContent = {}
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(initialContent);
  const [saving, setSaving] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [currentImageField, setCurrentImageField] = useState<string>('');

  useEffect(() => {
    loadContent();
  }, [section, pageId]);

  const loadContent = async () => {
    try {
      // In a real implementation, you would load content from the database
      // For now, we'll just use the initialContent
      if (Object.keys(initialContent).length > 0) {
        setContent(initialContent);
      }
    } catch (error) {
      console.error('Error loading content:', error);
      toast.error('Erreur lors du chargement du contenu');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // In a real implementation, you would save to the database
      // For now, we'll just simulate saving
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success('Contenu enregistré avec succès');
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Erreur lors de l\'enregistrement du contenu');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setContent(initialContent);
    setIsEditing(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContent(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleItemChange = (index: number, field: string, value: string) => {
    if (!content.items) return;
    
    const newItems = [...content.items];
    newItems[index] = {
      ...newItems[index],
      [field]: value
    };
    
    setContent(prev => ({
      ...prev,
      items: newItems
    }));
  };

  const handleAddItem = () => {
    if (!content.items) {
      setContent(prev => ({
        ...prev,
        items: [{
          id: Date.now().toString(),
          title: 'Nouvel élément',
          description: 'Description de l\'élément'
        }]
      }));
    } else {
      setContent(prev => ({
        ...prev,
        items: [
          ...prev.items || [],
          {
            id: Date.now().toString(),
            title: 'Nouvel élément',
            description: 'Description de l\'élément'
          }
        ]
      }));
    }
  };

  const handleRemoveItem = (index: number) => {
    if (!content.items) return;
    
    const newItems = [...content.items];
    newItems.splice(index, 1);
    
    setContent(prev => ({
      ...prev,
      items: newItems
    }));
  };

  const openMediaLibrary = (field: string) => {
    setCurrentImageField(field);
    setShowMediaLibrary(true);
  };

  const handleSelectImage = (url: string) => {
    if (currentImageField.startsWith('item-')) {
      const itemIndex = parseInt(currentImageField.split('-')[1]);
      const itemField = currentImageField.split('-')[2];
      
      handleItemChange(itemIndex, itemField, url);
    } else {
      setContent(prev => ({
        ...prev,
        [currentImageField]: url
      }));
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{section}</h3>
        {isEditing ? (
          <div className="flex gap-2">
            <button
              onClick={handleCancel}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              disabled={saving}
            >
              <X size={18} />
            </button>
            <button
              onClick={handleSave}
              className="p-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
              disabled={saving}
            >
              {saving ? (
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <Save size={18} />
              )}
            </button>
          </div>
        ) : (
          <button
            onClick={() => setIsEditing(true)}
            className="p-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
          >
            <Edit size={18} />
          </button>
        )}
      </div>

      <div className="space-y-4">
        {/* Title */}
        {content.title !== undefined && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
            {isEditing ? (
              <input
                type="text"
                name="title"
                value={content.title}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            ) : (
              <p className="text-gray-900">{content.title || 'Non défini'}</p>
            )}
          </div>
        )}

        {/* Subtitle */}
        {content.subtitle !== undefined && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sous-titre</label>
            {isEditing ? (
              <input
                type="text"
                name="subtitle"
                value={content.subtitle}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            ) : (
              <p className="text-gray-600">{content.subtitle || 'Non défini'}</p>
            )}
          </div>
        )}

        {/* Description */}
        {content.description !== undefined && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            {isEditing ? (
              <textarea
                name="description"
                value={content.description}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            ) : (
              <p className="text-gray-600">{content.description || 'Non défini'}</p>
            )}
          </div>
        )}

        {/* Image */}
        {content.image !== undefined && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
            {isEditing ? (
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <input
                    type="text"
                    name="image"
                    value={content.image}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="URL de l'image"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => openMediaLibrary('image')}
                  className="p-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  <Image size={18} />
                </button>
              </div>
            ) : (
              <div className="relative w-32 h-32 bg-gray-100 rounded-md overflow-hidden">
                {content.image ? (
                  <img
                    src={content.image}
                    alt="Preview"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150?text=Error';
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    <Image size={24} />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Items */}
        {content.items !== undefined && (
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">Éléments</label>
              {isEditing && (
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="p-1 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
                >
                  <Plus size={16} />
                </button>
              )}
            </div>
            
            <div className="space-y-4">
              {content.items && content.items.map((item, index) => (
                <div key={item.id} className="border border-gray-200 rounded-md p-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-gray-900">Élément {index + 1}</h4>
                    {isEditing && (
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Titre</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={item.title}
                          onChange={(e) => handleItemChange(index, 'title', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      ) : (
                        <p className="text-gray-900">{item.title || 'Non défini'}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                      {isEditing ? (
                        <textarea
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      ) : (
                        <p className="text-gray-600">{item.description || 'Non défini'}</p>
                      )}
                    </div>
                    
                    {item.image !== undefined && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Image</label>
                        {isEditing ? (
                          <div className="flex items-start gap-2">
                            <div className="flex-1">
                              <input
                                type="text"
                                value={item.image}
                                onChange={(e) => handleItemChange(index, 'image', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="URL de l'image"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => openMediaLibrary(`item-${index}-image`)}
                              className="p-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                            >
                              <Image size={18} />
                            </button>
                          </div>
                        ) : (
                          <div className="relative w-24 h-24 bg-gray-100 rounded-md overflow-hidden">
                            {item.image ? (
                              <img
                                src={item.image}
                                alt="Preview"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150?text=Error';
                                }}
                              />
                            ) : (
                              <div className="flex items-center justify-center h-full text-gray-400">
                                <Image size={20} />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {(!content.items || content.items.length === 0) && !isEditing && (
                <p className="text-gray-500 text-sm italic">Aucun élément</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Media Library Modal */}
      <MediaLibrary
        isOpen={showMediaLibrary}
        onClose={() => setShowMediaLibrary(false)}
        onSelect={handleSelectImage}
        title="Sélectionner une image"
      />
    </div>
  );
};

export default ContentManager;